// InstallableModalStub — Phase 3 placeholder modal.
//
// Mounted at each consuming app root alongside the existing Modal overlay.
// Opens whenever root.state._installableModal is set (by openInstallableModal).
// Closing clears that key.
//
// Phase 4 (MarketplaceInstallModal extensibility) replaces this component
// with the real install/manage flow. The stateKey '_installableModal' and the
// close pattern are the stable contract Phase 4 hooks into.
//
// Mount at app root (e.g. canvas/app.js, intranet/app.js):
//   InstallableModalStub: {}
//
// No additional props needed — reads everything from root.state._installableModal.

export const InstallableModalStub = {
  extends: 'Modal',
  stateKey: '_installableModal',

  Window: {
    extends: 'ModalWindow',
    width: 'H2',
    maxWidth: '90vw',

    ModalHeader: {
      flow: 'x',
      gap: 'Z',
      align: 'center space-between',
      padding: '0 0 Z 0',

      TitleGroup: {
        flow: 'y',
        gap: 'X2',

        Title: {
          tag: 'h2',
          margin: '0',
          fontSize: 'B',
          fontWeight: '700',
          color: 'title',
          text: (el, s) => {
            const data = s.root?._installableModal
            if (!data) return 'Install'
            const label = data.kind || data.domain || 'item'
            return data.installed
              ? `Manage ${label}`
              : `Install ${label}`
          }
        },

        Subtitle: {
          tag: 'p',
          margin: '0',
          fontSize: 'Z2',
          fontWeight: '400',
          color: 'caption',
          text: (el, s) => {
            const data = s.root?._installableModal
            if (!data) return ''
            const parts = []
            if (data.domain) parts.push(`Domain: ${data.domain}`)
            if (data.defaultScope?.type) {
              parts.push(`Scope: ${data.defaultScope.type}`)
            }
            return parts.join(' · ')
          }
        }
      },

      CloseButton: {
        tag: 'button',
        type: 'button',
        padding: 'Y',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: 'caption',
        round: 'Y',
        ':hover': { background: 'overlayStrong', color: 'title' },
        onClick: (e, el, s) => {
          s.root?.update({ _installableModal: null })
        },
        Icon: {
          name: 'close',
          fontSize: 'B'
        }
      }
    },

    // ── Phase 4 notice ────────────────────────────────────────────────────

    PhaseNotice: {
      flow: 'y',
      gap: 'Z',
      padding: 'Z A',
      round: 'A',
      background: 'blue.1',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'blue.3',

      NoticeRow: {
        flow: 'x',
        gap: 'Y',
        align: 'center left',

        Icon: {
          name: 'info',
          fontSize: 'Z2',
          color: 'blue'
        },

        Label: {
          tag: 'span',
          fontSize: 'Z2',
          fontWeight: '600',
          color: 'blue',
          text: 'Phase 4 — MarketplaceInstallModal coming soon'
        }
      },

      Description: {
        tag: 'p',
        margin: '0',
        fontSize: 'Y2',
        color: 'paragraph',
        lineHeight: '1.5',
        text: (el, s) => {
          const data = s.root?._installableModal
          if (!data) return ''
          return (
            `openInstallableModal was called with: domain="${data.domain || '—'}", ` +
            `kind="${data.kind || '—'}", installed=${!!data.installed}. ` +
            'Phase 4 MarketplaceInstallModal extensibility will wire this up with the ' +
            'full install / manage flow.'
          )
        }
      }
    },

    // ── Args debug display ────────────────────────────────────────────────

    ArgsDisplay: {
      flow: 'y',
      gap: 'Y',
      padding: 'Z A',
      round: 'A',
      background: 'overlayStrong',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'line',

      ArgsLabel: {
        tag: 'span',
        fontSize: 'Y1',
        fontWeight: '700',
        letterSpacing: '0.04em',
        color: 'caption',
        text: 'RECEIVED ARGS'
      },

      ArgsCode: {
        tag: 'pre',
        margin: '0',
        fontSize: 'Y2',
        color: 'paragraph',
        fontFamily: 'monospace',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        text: (el, s) => {
          const data = s.root?._installableModal
          return data ? JSON.stringify(data, null, 2) : '{}'
        }
      }
    },

    // ── Footer ────────────────────────────────────────────────────────────

    ModalFooter: {
      extends: 'ModalFooter',
      flow: 'x',
      gap: 'Y',
      align: 'center right',

      DismissButton: {
        tag: 'button',
        type: 'button',
        padding: 'Y Z2',
        round: 'Y',
        background: 'overlayStrong',
        border: 'none',
        cursor: 'pointer',
        fontSize: 'Z2',
        fontWeight: '600',
        color: 'title',
        text: 'Dismiss',
        ':hover': { background: 'line' },
        onClick: (e, el, s) => {
          s.root?.update({ _installableModal: null })
        }
      }
    }
  }
}
